import { defineComponent as _defineComponent } from 'vue'
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { SportsModuleFragment } from '@/features/onboarded-home/types';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import CompetitorLogo from '@/components/sports/common/CompetitorLogo.vue';
import GameEventStatus from '@/components/sports/common/chip/GameEventStatus.vue';
import NoOfferNotifier from '@/components/sports/common/event/NoOfferNotifier.vue';

import { ObjectType, SportEventStatus, SportOffer } from '@/@types/graphql-types';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';
import { SingleStepEventContext } from '@/helpers/sports/config';
import { formatDate, formatTime } from '@/helpers/time-zone-helper';
import { SnowplowContext, SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useI18n } from '@/helpers/composables/useI18n';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { useUserStore } from '@/helpers/composables/useStores';

import { computed } from 'vue';

type SingleStepSportEvent = AssertByTypename<SportsModuleFragment['events'][number], 'SingleStepSportEvent'>;


export default /*#__PURE__*/_defineComponent({
  __name: 'SportsModuleCard',
  props: {
    event: null,
    listIndex: null,
    context: null,
    additionalContexts: { default: () => [] }
  },
  emits: ["navigateClick"],
  setup(__props: any) {

const props = __props as { event: SingleStepSportEvent, listIndex: number, context: SingleStepEventContext, additionalContexts: SnowplowContext[] };





const { country, language, locales, webLocale } = useLanguageStore();
const { timeZone } = useUserStore();
const { xServeTrack } = useAppStore();
const { tc } = useI18n();

const componentContainer = computed(() => {
	if (props.event.content.fullPath) {
		return {
			cmp: 'RouterLink',
			props: {
				to: props.event.content.fullPath,
			},
		};
	}
	return {
		cmp: 'div',
	};
});

const sportContext = computed(() =>
	SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, props.event)
);

const competitors = computed(() => props.context.getCompetitors(props.event, webLocale.value));

const startName = computed(() => competitors.value.start.map(team => team.name).join(' / '));

const endName = computed(() => competitors.value.end.map(team => team.name).join(' / '));

const start = computed(() => competitors.value.start[0]);

const end = computed(() => competitors.value.end[0]);

const eventStatus = computed(() => props.event.content.status);

const isLive = computed(() => eventStatus.value === SportEventStatus.Live);

const isEnded = computed(() => [SportEventStatus.Ended, SportEventStatus.Closed].includes(eventStatus.value));

const competitionLink = computed(() => props.event.competition?.content.fullPath);

const activeStatus = [
	SportEventStatus.Cancelled,
	SportEventStatus.Closed,
	SportEventStatus.Delayed,
	SportEventStatus.Ended,
	SportEventStatus.Live,
	SportEventStatus.Postponed,
	SportEventStatus.Suspended,
];
const isNotUpcoming = computed(() => activeStatus.includes(eventStatus.value));

const eventDate = computed(() =>
	formatDate(new Date(props.event.content.startAt), locales.value, webLocale.value, timeZone.value, language.value)
);

const eventTime = computed(() =>
	formatTime(new Date(props.event.content.startAt), locales.value, webLocale.value, timeZone.value, language.value)
);

const watchNowOffer = computed(() => props.event.watchNowOffer as SportOffer | undefined);

const notifyMePayload = computed(() => {
	return {
		eventId: props.event.id,
		isNotificationActive: !!props.event.mysportlistEntry?.createdAt,
	};
});

function getMonetizationType(offer: SportOffer) {
	if (offer?.package?.packageId === 695 && country.value === 'FR') return tc('WEBAPP_FREE_TRIAL').toUpperCase();
	return offer.monetizationType;
}

const offerClickoutUrl = computed(() =>
	new ClickoutUrl(watchNowOffer.value)
		.addContexts(sportContext.value)
		.addContexts(props.additionalContexts)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('uct_sport', props.event?.sport)
		.setCanary(xServeTrack.value)
);

function handleClickOut(offer: SportOffer | undefined, listIndex: number) {
	if (offer == null) return;
	trackClickOutSports(offer, 0, listIndex, offer.sportObjectType, 'card-v2', [sportContext.value]);
}

function handleCardClick() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'event_clicked', label: 'card-module', value: props.listIndex, useLastRoute: true },
		[sportContext.value]
	);
}

function handleNoOfferNotifierClick() {
	TrackingHelper.trackEvent('userinteraction', { action: 'notify_me_clicked', property: 'card-v2' }, [
		sportContext.value,
	]);
}

return { __sfc: true,props, country, language, locales, webLocale, timeZone, xServeTrack, tc, componentContainer, sportContext, competitors, startName, endName, start, end, eventStatus, isLive, isEnded, competitionLink, activeStatus, isNotUpcoming, eventDate, eventTime, watchNowOffer, notifyMePayload, getMonetizationType, offerClickoutUrl, handleClickOut, handleCardClick, handleNoOfferNotifierClick, ProviderIcon, CompetitorLogo, GameEventStatus, NoOfferNotifier }
}

})